<template>
  <div
    class="inline-flex items-center gap-1 select-text group"
    :class="containerClass"
    v-bind="$attrs"
  >
    <span
      class="max-w-full cursor-pointer text-surface-700 dark:text-surface-200 hover:text-surface-900 dark:hover:text-surface-0 group-hover:text-rch-black dark:group-hover:text-surface-300 transition-colors"
      :style="{
        maxWidth: resolvedMaxWidth,
        display: 'inline-block',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }"
      :title="text ?? undefined"
      @click="copyToClipboard"
      data-test-id="copyable-truncated-text"
    >
      {{ displayText }}
    </span>
    <button
      type="button"
      class="shrink-0 inline-flex items-center justify-center rounded-md p-1 text-surface-400 dark:text-surface-500 hover:text-surface-600 dark:hover:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800 focus:outline-none focus:ring-2 focus:ring-rch-black"
      @click.stop="copyToClipboard"
      :aria-label="ariaLabel"
      data-test-id="copyable-icon-button"
    >
      <i
        :class="copied ? 'pi pi-check text-rch-black' : 'pi pi-copy'"
        class="text-sm group-hover:text-rch-black"
      ></i>
      <!-- The visible "Copied" flash below is not announced; this is. -->
      <span class="sr-only" role="status">{{ copied ? 'Copied' : '' }}</span>
    </button>
  </div>
  <transition name="fade">
    <div v-if="copied" class="text-xs text-rch-black mt-1">Copied</div>
  </transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useCopyToClipboard } from '@/composables/useCopyToClipboard'

defineOptions({
  inheritAttrs: false,
})

interface Props {
  text: string | number | null | undefined
  maxWidth?: string | number
  placeholder?: string
  ariaLabel?: string
  class?: string
  showStartChars?: number
  showEndChars?: number
}

const props = defineProps<Props>()
const emit = defineEmits<{ copied: [value: string] }>()

// The composable is this component's own copy logic, lifted out so the drawer
// panels could reuse it. Delegating keeps the clipboard fallback and the
// "copied" reset window in one place.
const { copied, copy } = useCopyToClipboard()

const resolvedMaxWidth = computed(() => {
  if (typeof props.maxWidth === 'number') return `${props.maxWidth}px`
  return props.maxWidth || '16rem'
})

const placeholder = computed(() => props.placeholder ?? '')
const ariaLabel = computed(() => props.ariaLabel ?? 'Copy to clipboard')

const containerClass = computed(() => props.class || '')

const displayText = computed(() => {
  const value = String(props.text ?? '')
  if (!value) return placeholder.value
  const start = props.showStartChars ?? 8
  const end = props.showEndChars ?? 8
  const minimum = start + end + 1
  if (value.length <= minimum) return value
  const prefix = value.slice(0, start)
  const suffix = value.slice(-end)
  return `${prefix}…${suffix}`
})

async function copyToClipboard() {
  const value = String(props.text ?? '')
  if (!value) return
  await copy(value)
  emit('copied', value)
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 150ms ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
